import { useAsyncState, useIntervalFn, whenever, useThrottleFn, useDebounceFn } from '@vueuse/core';
import type { Ref } from 'vue';
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import { ShortName } from '@getaccept/shared-utils';
import { useUserAgent } from '@getaccept/lib-shared/src/composables/user-agent.composable';
import { BankIdSwedenService } from '../../../api/signing/services/bank-id-sweden.service';
import { BankIdSwedenHelper } from '../helpers/bank-id-sweden.helper';
import type { State } from '../types/bank-id-sweden';
import type { BankIdStartResponse } from '../../types/bankid-se-dots';
import { REDIRECT_HASH_PREFIX } from './resume-eid-signing.composable';

export interface UseBankIdSwedenArgs {
  documentId: string;
  recipientId: string;
}

export const useBankIdSweden = ({ documentId, recipientId }: UseBankIdSwedenArgs) => {
  const { userAgent, isMobileOrTabletDevice, isIosOs } = useUserAgent();

  const controller: Ref<AbortController> = ref();
  const state: Ref<State> = ref('not_started');
  const statusCode: Ref<ShortName> = ref(null);

  const isFinished = computed(() => ['failed', 'completed'].includes(state.value));
  const isClosing = ref(false);

  const returnUrl = computed(() => {
    const url = `${window.location.href}#${REDIRECT_HASH_PREFIX}=true`;

    // On iOS, all links are opened in Safari. To re-open correct browser we need to use
    // the correct custom URL schemes.
    if (isIosOs.value) {
      switch (userAgent.value.browser.name) {
        case 'Chrome':
          return 'googlechromes://';
        case 'Firefox':
          return 'firefox://';
      }
    }

    // Return URL not needed for desktops, as no redirect occurs (application is opened instead)
    return isMobileOrTabletDevice.value ? url : null;
  });

  const appUrl = computed(() => {
    const prefix = isMobileOrTabletDevice.value ? 'https://app.bankid.com/' : 'bankid:///';
    return `${prefix}?autostarttoken=${
      startResponse.value?.autoStartToken
    }&redirect=${encodeURIComponent(returnUrl.value)}
    `;
  });

  const qrCode = computed(() => qrCodeRefresh.value || startResponse.value?.qrCode || null);

  const stop = () => {
    stopPolling();
    abortPendingRequests();
  };

  const start = useThrottleFn(() => {
    if (isClosing.value) {
      return;
    }
    executeStart();
  }, 1000);

  const restart = useThrottleFn(() => {
    stop();
    start();
  }, 1000);

  const handleRequestError = (error: any) => {
    if (error?.code === 'ERR_CANCELED') {
      return;
    }

    state.value = 'failed';
    statusCode.value = ShortName.RFA22;

    useDebounceFn(restart, 2000)();
  };

  whenever(isFinished, stop);

  const cancel = () => {
    state.value = 'cancelling';
    stop();

    if (startResponse.value?.orderRef) {
      executeCancel();
    }
  };

  const abortPendingRequests = () => {
    controller.value?.abort();
    controller.value = null;
  };

  const resume = (orderRef: string, autoStartToken: string) => {
    executeStart(0, orderRef, autoStartToken);
  };

  const { state: startResponse, execute: executeStart } = useAsyncState(
    (orderRef?: string, autoStartToken?: string): Promise<BankIdStartResponse> => {
      state.value = 'not_started';
      controller.value = new AbortController();

      return orderRef
        ? Promise.resolve({ orderRef, autoStartToken, qrCode: '' })
        : BankIdSwedenService.start(documentId, recipientId);
    },
    null,
    {
      immediate: false,
      onSuccess: () => {
        startPolling();
      },
      // TODO: Add error handling for 'document not found' and 'entity not found'
      onError: handleRequestError,
    }
  );

  const { execute: refreshStatus, error: statusRefreshError } = useAsyncState(
    async () =>
      await BankIdSwedenService.poll(
        startResponse.value?.orderRef,
        documentId,
        recipientId,
        controller.value?.signal
      ),
    null,
    {
      immediate: false,
      resetOnExecute: false,
      onSuccess: async response => {
        state.value = BankIdSwedenHelper.getStateFromStatus(response.status, response.shortName);
        statusCode.value = response.shortName;

        console.log({ state: state.value, statusCode: statusCode.value });

        if (!response.isValidTransaction) {
          await nextTick();
          restart();
        }
      },
      onError: handleRequestError,
    }
  );

  const {
    state: qrCodeRefresh,
    execute: refreshQrCode,
    error: qrCodeRefreshError,
  } = useAsyncState(
    () => BankIdSwedenService.reset(startResponse.value.orderRef, controller.value?.signal),
    null,
    {
      immediate: false,
      resetOnExecute: false,
      onError: handleRequestError,
    }
  );

  const { execute: executeCancel } = useAsyncState(
    () => BankIdSwedenService.cancel(startResponse.value?.orderRef),
    null,
    {
      immediate: false,
      resetOnExecute: true,
    }
  );

  const { pause: stopPolling, resume: startPolling } = useIntervalFn(
    () => {
      if (state.value === 'started') {
        refreshQrCode();
      }

      refreshStatus();
    },
    1000,
    { immediate: false, immediateCallback: true }
  );

  whenever(() => statusRefreshError.value || qrCodeRefreshError.value, restart);

  onBeforeUnmount(() => {
    isClosing.value = true;
    stop();
  });

  return {
    qrCode,
    state,
    statusCode,
    appUrl,
    start,
    cancel,
    resume,
  };
};
